import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SectionBar } from "@/components/ui/section-bar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/ui/status-badge";
import { useUndoableDelete } from "@/hooks/use-undoable-delete";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { teamKeys } from "@/lib/query";
import { cn } from "@/lib/utils";
import {
  type Period,
  periodArraySchema,
  regionRowArraySchema,
  rosterRowArraySchema,
  teamRowArraySchema,
} from "@/schemas/teams";

const SKELETON_ROWS = ["a", "b", "c", "d"] as const;

export function ClaimTeamHistoryPage() {
  const queryClient = useQueryClient();

  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [showAddPeriod, setShowAddPeriod] = useState(false);
  const [periodName, setPeriodName] = useState("");
  const [pendingPeriodDelete, setPendingPeriodDelete] = useState<Period | null>(null);

  const periodsQuery = useQuery({
    queryKey: teamKeys.periods,
    queryFn: () => apiGet("/api/periods", periodArraySchema),
  });

  const regionsQuery = useQuery({
    queryKey: teamKeys.historyRegions(selectedPeriodId),
    queryFn: () =>
      apiGet(
        `/api/claim-team-history/regions?period=${encodeURIComponent(selectedPeriodId ?? "")}`,
        regionRowArraySchema,
      ),
    enabled: selectedPeriodId !== null,
  });

  const teamsQuery = useQuery({
    queryKey: teamKeys.historyTeams(selectedPeriodId, selectedRegion),
    queryFn: () =>
      apiGet(
        `/api/claim-team-history/teams?period=${encodeURIComponent(
          selectedPeriodId ?? "",
        )}&region=${encodeURIComponent(selectedRegion ?? "")}`,
        teamRowArraySchema,
      ),
    enabled: selectedPeriodId !== null && selectedRegion !== null,
  });

  const rosterQuery = useQuery({
    queryKey: teamKeys.historyRoster(selectedPeriodId, selectedRegion, selectedTeam),
    queryFn: () => {
      const params = new URLSearchParams({ period: selectedPeriodId ?? "" });
      if (selectedRegion) params.set("region", selectedRegion);
      if (selectedTeam) params.set("team", selectedTeam);
      return apiGet(`/api/claim-team-history/roster?${params.toString()}`, rosterRowArraySchema);
    },
    enabled: selectedPeriodId !== null,
  });

  const selectedPeriod = periodsQuery.data?.find((p) => p.id === selectedPeriodId);

  // Auto-select the first claim group when a period's groups load. Cascades
  // into the team useEffect below, so picking a period populates the entire
  // page (Group → Team → Roster) without further clicks.
  useEffect(() => {
    const regions = regionsQuery.data;
    if (!regions || regions.length === 0) {
      if (selectedRegion !== null) setSelectedRegion(null);
      return;
    }
    if (!selectedRegion || !regions.some((r) => r.claimRegion === selectedRegion)) {
      setSelectedRegion(regions[0]?.claimRegion ?? null);
    }
  }, [regionsQuery.data, selectedRegion]);

  // Auto-select the first team when a group's teams load and no team is
  // selected (or the current selection is no longer in the list).
  useEffect(() => {
    const teams = teamsQuery.data;
    if (!teams || teams.length === 0) {
      if (selectedTeam !== null) setSelectedTeam(null);
      return;
    }
    if (!selectedTeam || !teams.some((t) => t.claimRegionTeam === selectedTeam)) {
      setSelectedTeam(teams[0]?.claimRegionTeam ?? null);
    }
  }, [teamsQuery.data, selectedTeam]);

  const addPeriodMutation = useMutation({
    mutationFn: (data: { name: string }) => apiSend("POST", "/api/periods", data, z.unknown()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.periods });
      toast.success("Period created");
      setShowAddPeriod(false);
      setPeriodName("");
    },
    onError: (error) => {
      logger.error("period_create_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to create period");
    },
  });

  const lockPeriodMutation = useMutation({
    mutationFn: (id: string) => apiSend("PUT", `/api/periods/${id}/lock`, undefined, z.unknown()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.periods });
      toast.success("Period locked");
    },
    onError: (error) => {
      logger.error("period_lock_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to lock period");
    },
  });

  const { pendingIds: pendingPeriodIds, enqueue: enqueuePeriodDelete } = useUndoableDelete<Period>({
    describe: (p) => `Deleted period "${p.name}"`,
    commit: (p) => apiSend("DELETE", `/api/periods/${p.id}`, undefined, z.unknown()),
    onCommitSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.periods });
    },
    onCommitError: (_, error) => {
      logger.error("period_delete_failed", { error });
    },
  });

  const handleSelectPeriod = (id: string) => {
    setSelectedPeriodId(id);
    setSelectedRegion(null);
    setSelectedTeam(null);
  };

  const handleSelectRegion = (region: string) => {
    setSelectedRegion(region);
    setSelectedTeam(null);
  };

  const teamsPanelTitle = selectedRegion ? `${selectedRegion} Teams` : "Teams (select a group)";
  const rosterPanelTitle = selectedTeam
    ? `${selectedTeam} Roster`
    : selectedRegion
      ? `${selectedRegion} Roster`
      : selectedPeriod
        ? `${selectedPeriod.name} Roster`
        : "Roster";

  return (
    <div className="space-y-4">
      <PageBreadcrumbs current="Team History" />
      {/* ─── Period selector strip ─── */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border bg-card px-3 py-2">
        <span className="text-muted-foreground text-sm">Period</span>
        <Select
          value={selectedPeriodId ?? ""}
          onValueChange={handleSelectPeriod}
          disabled={periodsQuery.isLoading || (periodsQuery.data?.length ?? 0) === 0}
        >
          <SelectTrigger className="h-8 w-[10rem]">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {periodsQuery.data
              ?.filter((p) => !pendingPeriodIds.has(p.id))
              .map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <span className="inline-flex items-center gap-1.5">
                    {p.name}
                    {p.isLocked && <Lock className="size-3 text-muted-foreground" />}
                  </span>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        {selectedPeriod && !selectedPeriod.isLocked && (
          <>
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-md px-2.5 text-xs"
              onClick={() => lockPeriodMutation.mutate(selectedPeriod.id)}
              disabled={lockPeriodMutation.isPending}
            >
              <Lock className="size-3.5" />
              Lock
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-md px-2.5 text-xs"
              onClick={() => setPendingPeriodDelete(selectedPeriod)}
            >
              <Trash2 className="size-3.5" />
              Delete
            </Button>
          </>
        )}

        {selectedPeriod?.isLocked && (
          <StatusBadge statusKey="restricted" label="Locked" withDot={false}>
            <Lock className="size-3" />
          </StatusBadge>
        )}

        <div className="ml-auto">
          {showAddPeriod ? (
            <div className="flex items-center gap-2">
              <Input
                placeholder="YYYY-MM"
                value={periodName}
                onChange={(e) => setPeriodName(e.target.value)}
                className="h-8 w-32"
              />
              <Button
                size="sm"
                className="h-7"
                disabled={addPeriodMutation.isPending || !periodName.trim()}
                onClick={() => addPeriodMutation.mutate({ name: periodName })}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7"
                onClick={() => setShowAddPeriod(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-md px-2.5 text-xs"
              onClick={() => {
                setShowAddPeriod(true);
                setPeriodName("");
              }}
            >
              <Plus className="size-3.5" />
              Add Period
            </Button>
          )}
        </div>
      </div>

      {/* ─── Main grid ─── */}
      <div className="grid gap-4 lg:grid-cols-12">
        {/* LEFT: Claim Groups + Teams stacked */}
        <div className="space-y-4 lg:col-span-4">
          {/* Claim Groups */}
          <section>
            <SectionBar
              title="Claim Groups"
              subtitle={selectedPeriodId ? `${regionsQuery.data?.length ?? 0} rows` : undefined}
            />
            <div className="rounded-b-md border border-t-0 bg-card p-3">
              {!selectedPeriodId ? (
                <p className="py-4 text-center text-muted-foreground text-sm">
                  Select a period to view its claim groups.
                </p>
              ) : regionsQuery.isLoading ? (
                <div className="space-y-2">
                  {SKELETON_ROWS.map((k) => (
                    <div key={k} className="h-8 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground text-xs">
                      <th className="px-1.5 pb-1 font-medium">Claim Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regionsQuery.data?.map((r) => (
                      <tr
                        key={r.claimRegion}
                        className={cn(
                          "cursor-pointer border-b last:border-0 hover:bg-muted/50",
                          selectedRegion === r.claimRegion &&
                            "bg-primary/10 font-medium text-primary",
                        )}
                        onClick={() => handleSelectRegion(r.claimRegion)}
                      >
                        <td className="px-1.5 py-1">{r.claimRegion}</td>
                      </tr>
                    ))}
                    {regionsQuery.data?.length === 0 && (
                      <tr>
                        <td className="py-4 text-center text-muted-foreground text-xs">
                          No groups in this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* Teams */}
          <section>
            <SectionBar
              title={teamsPanelTitle}
              subtitle={selectedRegion ? `${teamsQuery.data?.length ?? 0} rows` : undefined}
            />
            <div className="rounded-b-md border border-t-0 bg-card p-3">
              {!selectedRegion ? (
                <p className="py-4 text-center text-muted-foreground text-sm">
                  Select a claim group above to view its teams.
                </p>
              ) : teamsQuery.isLoading ? (
                <div className="space-y-2">
                  {SKELETON_ROWS.map((k) => (
                    <div key={k} className="h-8 animate-pulse rounded bg-muted" />
                  ))}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground text-xs">
                      <th className="px-1.5 pb-1 font-medium">Claim Team</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamsQuery.data?.map((t) => (
                      <tr
                        key={t.claimRegionTeam}
                        className={cn(
                          "cursor-pointer border-b last:border-0 hover:bg-muted/50",
                          selectedTeam === t.claimRegionTeam &&
                            "bg-primary/10 font-medium text-primary",
                        )}
                        onClick={() => setSelectedTeam(t.claimRegionTeam)}
                      >
                        <td className="px-1.5 py-1">{t.claimRegionTeam}</td>
                      </tr>
                    ))}
                    {teamsQuery.data?.length === 0 && (
                      <tr>
                        <td className="py-4 text-center text-muted-foreground text-xs">
                          No teams in this group.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>

        {/* RIGHT: Roster */}
        <div className="lg:col-span-8">
          <SectionBar
            title={rosterPanelTitle}
            subtitle={selectedPeriodId ? `${rosterQuery.data?.length ?? 0} rows` : undefined}
          />
          <div className="rounded-b-md border border-t-0 bg-card p-3">
            {!selectedPeriodId ? (
              <p className="py-4 text-center text-muted-foreground text-sm">
                Select a period above to view its roster.
              </p>
            ) : rosterQuery.isLoading ? (
              <div className="space-y-2">
                {SKELETON_ROWS.map((k) => (
                  <div key={k} className="h-8 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground text-xs">
                      <th className="px-1.5 pb-1 font-medium">Claim Group</th>
                      <th className="px-1.5 pb-1 font-medium">Claim Team</th>
                      <th className="px-1.5 pb-1 font-medium">User ID</th>
                      <th className="px-1.5 pb-1 font-medium">Title</th>
                      <th className="px-1.5 pb-1 text-center font-medium">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rosterQuery.data?.map((r) => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="px-1.5 py-1">{r.claimRegion}</td>
                        <td className="px-1.5 py-1">{r.claimRegionTeam}</td>
                        <td className="px-1.5 py-1">{r.userId}</td>
                        <td className="px-1.5 py-1">{r.title}</td>
                        <td className="px-1.5 py-1 text-center">
                          <StatusBadge
                            statusKey={r.isActive ? "policy-active" : "policy-expired"}
                            label={r.isActive ? "Yes" : "No"}
                          />
                        </td>
                      </tr>
                    ))}
                    {rosterQuery.data?.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 text-center text-muted-foreground text-xs">
                          No members for this selection.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={pendingPeriodDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingPeriodDelete(null);
        }}
        title="Delete period?"
        description={
          pendingPeriodDelete
            ? `This will remove the "${pendingPeriodDelete.name}" period and its roster history. You'll have 10 seconds to undo from the toast.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingPeriodDelete) {
            enqueuePeriodDelete(pendingPeriodDelete);
            // Clear the selection chain since the period the user was
            // viewing is now in the undo window (filtered out below).
            if (selectedPeriodId === pendingPeriodDelete.id) {
              setSelectedPeriodId(null);
              setSelectedRegion(null);
              setSelectedTeam(null);
            }
          }
          setPendingPeriodDelete(null);
        }}
      />
    </div>
  );
}
